import { useState, useRef, useEffect } from "react";
import TiltCard from "@/components/base/TiltCard";
import { SITE_MEDIA } from "@/constants/siteMedia";

const trustCards = [
  {
    icon: "ri-lock-password-line",
    iconColor: "#10b981",
    iconBg: "#f0fdf4",
    barColor: "#10b981",
    title: "Private by Design",
    description: "Your conversations are personal. Ava is built to keep them that way.",
  },
  {
    icon: "ri-database-2-line",
    iconColor: "#e11d48",
    iconBg: "#fff1f2",
    barColor: "#e11d48",
    title: "No Selling Your Data",
    description: "We do not sell, share, or exploit your personal conversations. Ever.",
  },
  {
    icon: "ri-shield-check-line",
    iconColor: "#0891b2",
    iconBg: "#ecfeff",
    barColor: "#0891b2",
    title: "Not Used Against You",
    description: "Your chats are not used to manipulate, target, or profile you.",
  },
  {
    icon: "ri-hand-heart-line",
    iconColor: "#d97706",
    iconBg: "#fff8e8",
    barColor: "#d97706",
    title: "You’re in Control",
    description: "Delete your conversations anytime. You decide what stays and what goes.",
  },
  {
    icon: "ri-heart-3-line",
    iconColor: "#ec4899",
    iconBg: "#fdf2f8",
    barColor: "#ec4899",
    title: "Built for Emotional Safety",
    description: "Ava is designed to support—not judge, pressure, or harm.",
  },
];

function TrustBotAnimation() {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 50);
    return () => clearInterval(id);
  }, []);

  const floatY = Math.sin(tick * 0.04) * 10;

  return (
    <div className="relative flex items-center justify-center" style={{ width: 280, height: 280 }}>
      <style>{`
        @keyframes trustGlow {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.38; transform: scale(1.08); }
        }
        @keyframes trustBadge1 {
          0%, 100% { transform: translateY(0px) rotate(-2deg); }
          50% { transform: translateY(-10px) rotate(2deg); }
        }
        @keyframes trustBadge2 {
          0%, 100% { transform: translateY(0px) rotate(2deg); }
          50% { transform: translateY(10px) rotate(-2deg); }
        }
        @keyframes trustBadge3 {
          0%, 100% { transform: translateY(0px) rotate(-1deg); }
          50% { transform: translateY(-7px) rotate(3deg); }
        }
        @keyframes trustRing {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes trustPulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.4); opacity: 1; }
        }
      `}</style>

      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: "radial-gradient(circle, #C084FC 0%, #7C3AED 35%, transparent 70%)",
          animation: "trustGlow 3s ease-in-out infinite",
        }}
      />

      <div
        className="absolute rounded-full border border-dashed border-[#A855F7]/25"
        style={{
          width: 250,
          height: 250,
          top: "50%",
          left: "50%",
          animation: "trustRing 22s linear infinite",
        }}
      />

      <div
        className="absolute top-2 left-1/2 -translate-x-1/2 bg-white rounded-xl px-3 py-2 border border-[#d1fae5] flex items-center gap-2 z-20"
        style={{ animation: "trustBadge1 2.8s ease-in-out infinite" }}
      >
        <div className="w-5 h-5 flex items-center justify-center rounded-lg bg-[#f0fdf4]">
          <i className="ri-database-2-line text-xs text-[#10b981]"></i>
        </div>
        <p className="text-xs font-bold text-[#1a1040] whitespace-nowrap">No data selling</p>
      </div>

      <div
        className="absolute bottom-6 left-0 bg-white rounded-xl px-3 py-2 border border-[#ede8ff] flex items-center gap-2 z-20"
        style={{ animation: "trustBadge2 3.4s ease-in-out infinite" }}
      >
        <div className="w-5 h-5 flex items-center justify-center rounded-lg bg-[#f5f0ff]">
          <i className="ri-hand-heart-line text-xs text-[#7C3AED]"></i>
        </div>
        <p className="text-xs font-bold text-[#1a1040] whitespace-nowrap">Judgement free</p>
      </div>

      <div
        className="absolute bottom-6 right-0 bg-white rounded-xl px-3 py-2 border border-[#ede8ff] flex items-center gap-2 z-20"
        style={{ animation: "trustBadge3 2.2s ease-in-out infinite" }}
      >
        <div className="w-5 h-5 flex items-center justify-center rounded-lg bg-[#f5f0ff]">
          <i className="ri-shield-check-line text-xs text-[#7C3AED]"></i>
        </div>
        <p className="text-xs font-bold text-[#1a1040] whitespace-nowrap">Always safe</p>
      </div>

      <div
        className="relative z-10"
        style={{ transform: `translateY(${floatY}px)`, transition: "transform 0.05s linear" }}
      >
        <img
          src={SITE_MEDIA.avaIllustration}
          alt="Ava AI Bot"
          className="w-36 h-36 md:w-44 md:h-44 object-contain"
          style={{ filter: "drop-shadow(0 0 28px rgba(168,85,247,0.4))" }}
        />
      </div>

      <div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full z-10 bg-[#A855F7]"
        style={{ animation: "trustPulse 2s ease-in-out infinite" }}
      />
    </div>
  );
}

export default function TrustSafety() {
  const [hovered, setHovered] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("trust-visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    const items = sectionRef.current?.querySelectorAll(".trust-animate");
    items?.forEach((c) => observer.observe(c));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="trust" className="py-14 md:py-20 bg-white">
      <style>{`
        .trust-animate {
          opacity: 0;
          transform: translateY(28px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .trust-animate.trust-visible {
          opacity: 1;
          transform: translateY(0);
        }
        .trust-animate:nth-child(1) { transition-delay: 0s; }
        .trust-animate:nth-child(2) { transition-delay: 0.1s; }
        .trust-animate:nth-child(3) { transition-delay: 0.2s; }
        .trust-animate:nth-child(4) { transition-delay: 0.3s; }
        .trust-card-hover {
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
        }
        .trust-card-hover:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 32px rgba(124,58,237,0.10);
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Centered header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-[#f5f0ff] text-[#7C3AED] text-sm font-medium mb-4">
            <i className="ri-shield-check-line"></i>
            TRUST
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-3 leading-tight">
            You are
            <br />
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}
            >
              Safe here
            </span>
          </h2>
          <p className="text-[#5a4a8a] text-base max-w-lg mx-auto">
            Talking to Ava should feel natural—and completely secure. Your privacy and trust come first in everything we build.
          </p>
        </div>

        {/* Two-column: left animation (narrower), right cards */}
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
          <div className="w-full lg:w-[280px] flex-shrink-0 flex justify-center trust-animate">
            <TrustBotAnimation />
          </div>

          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {trustCards.map((card, i) => (
              <TiltCard
                key={i}
                className="trust-animate"
                tiltAmount={8}
                scale={1.02}
              >
                <div
                  className="trust-card-hover bg-white rounded-2xl p-6 border cursor-default relative overflow-hidden h-full"
                  style={{ borderColor: hovered === i ? card.iconColor + "40" : "#ede8ff" }}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl mb-4 transition-all duration-300"
                    style={{ background: hovered === i ? card.iconColor : card.iconBg }}
                  >
                    <i
                      className={`${card.icon} text-xl transition-colors duration-300`}
                      style={{ color: hovered === i ? "#ffffff" : card.iconColor }}
                    ></i>
                  </div>
                  <h3 className="font-bold text-[#1a1040] text-base mb-2">{card.title}</h3>
                  <p className="text-[#7a6aaa] text-sm leading-relaxed">{card.description}</p>
                  <div
                    className="absolute bottom-0 left-0 h-[3px] rounded-full transition-all duration-500"
                    style={{ background: card.barColor, width: hovered === i ? "100%" : "32px" }}
                  />
                </div>
              </TiltCard>
            ))}
          </div>
        </div>

        {/* Bottom line */}
        <div className="text-center mt-14 trust-animate">
          <p className="text-[#7a6aaa] text-sm font-medium">
            Built with care by Neuroava.
          </p>
        </div>
      </div>
    </section>
  );
}